/**
 * Static agent records for the Agent Management preview (APD-1867).
 *
 * This file is the single source of truth for prototype data shared by
 * AgentInventory and AgentDetailContent until the real API is wired up.
 */

export type AgentCriticality = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

export type McpToolCallRecord = {
    service: string;
    count: number;
};

export type FindingSeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO';
export type FindingStatus = 'OPEN' | 'CLOSED';

export type FindingRecord = {
    finding: string;
    severity: FindingSeverity;
    status: FindingStatus;
    discovered: string;
    description: string;
    category: string;
};

/** One device install of an endpoint-discovered harness (APD-2006). */
export type HarnessInstallRecord = {
    deviceSn: string;
    rootPath: string;
    version: string;
    lastSeenAt: string;
};

/** An MCP server configured for (or seen running under) a harness. Env/header key NAMES only. */
export type ConfiguredMcpServerRecord = {
    name: string;
    scope: string;
    transport: string;
    target: string;
    envKeys: string[];
    headerKeys: string[];
    deviceSn: string;
    running: boolean;
};

export type AgentRecord = {
    /** Internal stable ID used as the URL param on the detail route */
    id: string;
    /** ROUTINE / SESSION from AI vendors or endpoint transcripts; HARNESS for endpoint-discovered harnesses */
    kind?: string;
    /** HARNESS agents: every device install for the owner */
    installs?: HarnessInstallRecord[];
    /** HARNESS agents: MCP servers configured across the owner's installs */
    configuredMcpServers?: ConfiguredMcpServerRecord[];
    /** External vendor-assigned identifier */
    agentId: string;
    /**
     * Human-readable display name for the agent session. For Claude Cowork Routines
     * this is the routine name (e.g. "Ops Run (Weekdays)"); for other sessions it is
     * the session name or product-surface identifier.
     */
    displayName: string;
    /**
     * Stable automation trigger ID — present only for sessions triggered by a
     * scheduled routine (e.g. Claude Cowork). Used to group runs of the same routine.
     */
    routineId?: string;
    /** Platform user ID of the agent owner — used to filter findings by user */
    userId?: string;
    ownerName: string;
    ownerEmail: string;
    ownerDepartment: string;
    vendor: string;
    model: string;
    harness: string;
    discoveredAt: string;
    runFrequency: string;
    lastRanAt: string;
    mcpServers: string[];
    mcpToolCalls: McpToolCallRecord[];
    findings: FindingRecord[];
    /** Number of sessions for this agent (from rollup); defaults to 1 for single sessions */
    sessionCount: number;
    // Run stats (last run)
    /** Total MCP tool calls made in the last run */
    totalToolCalls: number;
    /** Number of assistant turns in the last run */
    assistantTurns: number;
    /** Number of user turns in the last run */
    userTurns: number;
    /** Highest number of tool calls made between consecutive user messages */
    maxToolCallsBetweenUserMessages: number;
    /** Pre-computed autonomy score (0–100) from _statistics.autonomy_score */
    autonomyScore?: number;
    /** All MCP tool names used in this session, with the leading `mcp__` prefix stripped */
    mcpToolNames?: string[];
    /** Number of tool-result turns in the last run, from _statistics.tool_result_turns */
    toolResultTurns?: number;
    // Platform-inferred / attestation fields
    purpose: string;
    accessesSensitiveData: boolean;
    criticality: AgentCriticality;
    /** Human-readable duration, e.g. "4 min" */
    runDuration: string;
    authorization: string;
    certifiedByOwner: boolean;
    certificationDate: string;
};

/**
 * Naive autonomy score (0 – 100) measuring how autonomously an agent operated
 * in its last run, based on:
 *  - density:  tool calls per user message (capped at 5 calls = 100%)
 *  - burst:    longest uninterrupted tool-call chain (capped at 8 = 100%)
 *  - autonomy: fraction of turns that were assistant-driven (not user-driven)
 *
 * Weights: density 45%, burst 35%, autonomy 20%
 */
export const computeAutonomyScore = (
    agent: Pick<AgentRecord, 'userTurns' | 'totalToolCalls' | 'maxToolCallsBetweenUserMessages' | 'assistantTurns'>,
): number => {
    const { userTurns, totalToolCalls, maxToolCallsBetweenUserMessages, assistantTurns } = agent;
    const density = Math.min(totalToolCalls / Math.max(userTurns, 1) / 5, 1);
    const burst = Math.min(maxToolCallsBetweenUserMessages / 8, 1);
    const autonomy = 1 - userTurns / Math.max(userTurns + assistantTurns, 1);
    const score = 100 * (0.45 * density + 0.35 * burst + 0.2 * autonomy);
    return Math.round(score * 10) / 10;
};

export const autonomyScoreLabel = (score: number): string => {
    if (score < 20) {
        return 'Conversational';
    }
    if (score < 45) {
        return 'Tool-assisted';
    }
    if (score < 70) {
        return 'Semi-autonomous';
    }
    return 'Autonomous';
};

export const STATIC_AGENTS: AgentRecord[] = [
    {
        id: '1',
        agentId: 'asst_Kz9mXpR2wQv7nLtJ',
        displayName: 'Sprint Planning Assistant',
        ownerName: 'Sarah Chen',
        ownerEmail: 'sarah.chen@acme.com',
        ownerDepartment: 'Engineering',
        vendor: 'OpenAI',
        harness: 'ChatGPT',
        model: 'gpt-5.6-luna',
        discoveredAt: '2026-07-14',
        runFrequency: 'Daily',
        lastRanAt: '2026-09-02T18:32:00Z',
        mcpServers: ['jira-mcp', 'github-mcp'],
        mcpToolCalls: [
            { service: 'Jira', count: 424 },
            { service: 'GitHub', count: 117 },
            { service: 'Slack', count: 78 },
            { service: 'Notion', count: 45 },
        ],
        findings: [
            {
                finding: 'AI Agent Purpose is Unknown',
                severity: 'MEDIUM',
                status: 'CLOSED',
                discovered: '2026-09-03',
                description:
                    'This AI Agent must be vouched for by its creator by stating its purpose/objectives and its business criticality.',
                category: 'Agent Attestation',
            },
            {
                finding: 'New AI Agent Tool User Created',
                severity: 'INFO',
                status: 'CLOSED',
                discovered: '2026-09-02',
                description:
                    'A new user has been added to an AI service that can create AI agents capable of undertaking unsupervised, autonomous actions.',
                category: 'Agent Provisioning',
            },
        ],
        sessionCount: 1,
        totalToolCalls: 20,
        assistantTurns: 42,
        userTurns: 5,
        maxToolCallsBetweenUserMessages: 7,
        autonomyScore: 84.5,
        mcpToolNames: [
            'jira__create_issue',
            'jira__update_issue',
            'github__list_pull_requests',
            'slack__post_message',
            'notion__search_pages',
        ],
        toolResultTurns: 18,
        purpose: 'Automates Jira ticket triage and sprint planning summaries for the platform team.',
        accessesSensitiveData: true,
        criticality: 'HIGH',
        runDuration: '4 min',
        authorization: 'Approved',
        certifiedByOwner: true,
        certificationDate: '2026-08-01',
    },
    {
        id: '2',
        agentId: 'asst_Pq4yRzH8mNx2vCwK',
        displayName: 'Security Alert Monitor',
        ownerName: 'Marcus Williams',
        ownerEmail: 'marcus.williams@acme.com',
        ownerDepartment: 'Security',
        vendor: 'OpenAI',
        harness: 'Codex',
        model: 'gpt-codex-5.3',
        discoveredAt: '2026-07-22',
        runFrequency: 'Hourly',
        lastRanAt: '2026-09-03T13:15:00Z',
        mcpServers: ['okta-mcp', 'crowdstrike-mcp'],
        mcpToolCalls: [
            { service: 'Okta', count: 304 },
            { service: 'CrowdStrike', count: 223 },
            { service: 'Slack', count: 156 },
            { service: 'PagerDuty', count: 89 },
            { service: 'Datadog', count: 49 },
        ],
        findings: [
            {
                finding: 'AI Agent Purpose is Unknown',
                severity: 'MEDIUM',
                status: 'CLOSED',
                discovered: '2026-09-03',
                description:
                    'This AI Agent must be vouched for by its creator by stating its purpose/objectives and its business criticality.',
                category: 'Agent Attestation',
            },
            {
                finding: 'New AI Agent Tool User Created',
                severity: 'INFO',
                status: 'CLOSED',
                discovered: '2026-09-02',
                description:
                    'A new user has been added to an AI service that can create AI agents capable of undertaking unsupervised, autonomous actions.',
                category: 'Agent Provisioning',
            },
        ],
        sessionCount: 1,
        totalToolCalls: 48,
        assistantTurns: 44,
        userTurns: 4,
        maxToolCallsBetweenUserMessages: 10,
        autonomyScore: 98.3,
        mcpToolNames: [
            'okta__list_users',
            'okta__get_user',
            'crowdstrike__get_detections',
            'slack__post_message',
            'pagerduty__create_incident',
            'datadog__query_metrics',
        ],
        toolResultTurns: 44,
        purpose: 'Monitors identity anomalies and drafts incident summaries for the security team.',
        accessesSensitiveData: true,
        criticality: 'CRITICAL',
        runDuration: '2 min',
        authorization: 'Approved',
        certifiedByOwner: true,
        certificationDate: '2026-08-15',
    },
    {
        id: '3',
        agentId: 'claude-agent-7Xt3QsLmYpW9bDrF',
        displayName: 'Ops Run (Weekdays)',
        routineId: 'trig_01JgoF4NZ5xRFzEQAEQZrJVY',
        ownerName: 'Priya Anand',
        ownerEmail: 'priya.anand@acme.com',
        ownerDepartment: 'Product',
        vendor: 'Anthropic',
        harness: 'Claude Cowork',
        model: 'claude-4.5-sonnet',
        discoveredAt: '2026-08-01',
        runFrequency: 'Weekly',
        lastRanAt: '2026-08-30T09:00:00Z',
        mcpServers: ['notion-mcp'],
        mcpToolCalls: [
            { service: 'Notion', count: 167 },
            { service: 'Slack', count: 73 },
            { service: 'Google Drive', count: 22 },
        ],
        findings: [
            {
                finding: 'AI Agent Purpose is Unknown',
                severity: 'MEDIUM',
                status: 'OPEN',
                discovered: '2026-09-03',
                description:
                    'This AI Agent must be vouched for by its creator by stating its purpose/objectives and its business criticality.',
                category: 'Agent Attestation',
            },
            {
                finding: 'New AI Agent Tool User Created',
                severity: 'INFO',
                status: 'CLOSED',
                discovered: '2026-09-02',
                description:
                    'A new user has been added to an AI service that can create AI agents capable of undertaking unsupervised, autonomous actions.',
                category: 'Agent Provisioning',
            },
        ],
        sessionCount: 1,
        totalToolCalls: 20,
        assistantTurns: 12,
        userTurns: 14,
        maxToolCallsBetweenUserMessages: 4,
        autonomyScore: 39.6,
        mcpToolNames: ['notion__search_pages', 'notion__get_page', 'slack__post_message', 'google-drive__search_files'],
        toolResultTurns: 19,
        purpose: 'Generates weekly product metrics digests from Notion workspaces.',
        accessesSensitiveData: false,
        criticality: 'LOW',
        runDuration: '6 min',
        authorization: 'Approved',
        certifiedByOwner: false,
        certificationDate: '',
    },
    {
        id: '4',
        agentId: 'asst_Vn5kBjT1wEg6yUhO',
        displayName: 'Expense Reconciler',
        ownerName: 'Jordan Park',
        ownerEmail: 'jordan.park@acme.com',
        ownerDepartment: 'Finance',
        vendor: 'OpenAI',
        model: 'gpt-5.6-terra',
        harness: 'ChatGPT',
        discoveredAt: '2026-08-10',
        runFrequency: 'Monthly',
        lastRanAt: '2026-09-01T06:00:00Z',
        mcpServers: ['netsuite-mcp', 'google-drive-mcp'],
        mcpToolCalls: [
            { service: 'MySQL', count: 367 },
            { service: 'Gmail', count: 145 },
            { service: 'Google Drive', count: 101 },
            { service: 'Slack', count: 48 },
            { service: 'Okta', count: 31 },
        ],
        findings: [
            {
                finding: 'AI Agent Purpose is Unknown',
                severity: 'MEDIUM',
                status: 'OPEN',
                discovered: '2026-09-03',
                description:
                    'This AI Agent must be vouched for by its creator by stating its purpose/objectives and its business criticality.',
                category: 'Agent Attestation',
            },
            {
                finding: 'New AI Agent Tool User Created',
                severity: 'INFO',
                status: 'CLOSED',
                discovered: '2026-09-02',
                description:
                    'A new user has been added to an AI service that can create AI agents capable of undertaking unsupervised, autonomous actions.',
                category: 'Agent Provisioning',
            },
        ],
        sessionCount: 1,
        totalToolCalls: 24,
        assistantTurns: 24,
        userTurns: 10,
        maxToolCallsBetweenUserMessages: 5,
        autonomyScore: 57.6,
        mcpToolNames: [
            'mysql__query',
            'mysql__execute',
            'gmail__send_email',
            'google-drive__list_files',
            'slack__post_message',
        ],
        toolResultTurns: 22,
        purpose: 'Reconciles monthly expense reports and flags policy violations.',
        accessesSensitiveData: true,
        criticality: 'MEDIUM',
        runDuration: '12 min',
        authorization: 'Pending Review',
        certifiedByOwner: false,
        certificationDate: '',
    },
    {
        id: '5',
        agentId: 'claude-agent-2Rm8NhCvZpA4xKyE',
        displayName: 'On-Call Incident Responder',
        ownerName: 'Elena Russo',
        ownerEmail: 'elena.russo@acme.com',
        ownerDepartment: 'Engineering',
        vendor: 'Anthropic',
        harness: 'Claude Code',
        model: 'claude-opus-5',
        discoveredAt: '2026-08-18',
        runFrequency: 'On Demand',
        lastRanAt: '2026-09-02T22:10:00Z',
        mcpServers: ['github-mcp', 'datadog-mcp', 'pagerduty-mcp'],
        mcpToolCalls: [
            { service: 'Datadog', count: 200 },
            { service: 'PagerDuty', count: 160 },
            { service: 'GitHub', count: 120 },
            { service: 'Slack', count: 110 },
            { service: 'MySQL', count: 94 },
        ],
        findings: [
            {
                finding: 'AI Agent Purpose is Unknown',
                severity: 'MEDIUM',
                status: 'CLOSED',
                discovered: '2026-09-03',
                description:
                    'This AI Agent must be vouched for by its creator by stating its purpose/objectives and its business criticality.',
                category: 'Agent Attestation',
            },
            {
                finding: 'New AI Agent Tool User Created',
                severity: 'INFO',
                status: 'CLOSED',
                discovered: '2026-09-02',
                description:
                    'A new user has been added to an AI service that can create AI agents capable of undertaking unsupervised, autonomous actions.',
                category: 'Agent Provisioning',
            },
        ],
        sessionCount: 1,
        totalToolCalls: 28,
        assistantTurns: 40,
        userTurns: 8,
        maxToolCallsBetweenUserMessages: 6,
        autonomyScore: 74.5,
        mcpToolNames: [
            'datadog__query_metrics',
            'datadog__list_monitors',
            'pagerduty__create_incident',
            'github__list_commits',
            'slack__post_message',
            'mysql__query',
        ],
        toolResultTurns: 26,
        purpose: 'Triages on-call alerts, correlates Datadog traces, and creates PagerDuty incidents.',
        accessesSensitiveData: true,
        criticality: 'CRITICAL',
        runDuration: '8 min',
        authorization: 'Approved',
        certifiedByOwner: true,
        certificationDate: '2026-08-20',
    },
];
